import { useI18n } from '@/i18n/I18nProvider'
import { localePath } from '@/app/routes'
import { NEWSLETTER_ARCHIVE } from '@/content/newsletterIssues'
import { issueDateLabel } from '@/lib/issueDate'
import type { SectionOf } from './shared'

/**
 * The newsletter archive.
 *
 * A list, not a card grid. The e-books and reports indexes are grids because
 * every entry has a cover and the cover is most of why somebody clicks one; an
 * issue has a date, a title and two lines, and three of those in a square tile
 * is a grid of text boxes. Newest first, dated on the left, so the reader's
 * question — "did I miss one?" — is answered by scanning one column.
 *
 * The words come from the dictionary and the paths from the route registry
 * (`content/newsletterIssues.ts` resolves them), so nothing on a card is
 * written twice and none of it can disagree with the page it opens. A Turkish
 * reader gets the Turkish title the moment somebody writes
 * `content/pages/tr/newsletter-<slug>.ts`, and the English one until then,
 * which is this dictionary's ordinary rule.
 */
export default function IssuesSection({ section }: { section: SectionOf<'issues'> }) {
  const { t, locale } = useI18n()

  return (
    <>
      {section.heading && (
        <h2 className="max-w-4xl text-3xl font-bold tracking-tight sm:text-4xl">
          {section.heading}
        </h2>
      )}
      {section.sub && (
        <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{section.sub}</p>
      )}

      {NEWSLETTER_ARCHIVE.length === 0 ? (
        // Not a hidden section. A newsletter page that shows nothing where the
        // issues go reads as broken, and the footer's subscribe box is the
        // whole point of the page until there is a first issue to read.
        <p className="mt-10 max-w-3xl text-lg leading-relaxed text-muted-foreground">
          {section.empty}
        </p>
      ) : (
        <ul className="mt-10 divide-y divide-border border-t border-border">
          {NEWSLETTER_ARCHIVE.map(({ issue, route }) => {
            const page = t.pages[route.id]
            return (
              <li key={route.id}>
                <a
                  href={localePath(locale, route.path)}
                  className="group block py-7 sm:grid sm:grid-cols-[10rem_1fr] sm:gap-8"
                >
                  {/* `dateTime` carries the machine-readable form, which is the
                      one thing the label deliberately throws away — the label
                      is a month and a year because that is how a monthly
                      newsletter is referred to. */}
                  <time
                    dateTime={issue.date}
                    className="block text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground"
                  >
                    {issueDateLabel(issue.date, locale)}
                  </time>
                  <div className="mt-2 sm:mt-0">
                    <h3 className="text-xl font-bold leading-snug tracking-tight transition-colors duration-200 group-hover:text-primary">
                      {page.title}
                    </h3>
                    {page.description && (
                      <p className="mt-2 text-base leading-relaxed text-muted-foreground">
                        {page.description}
                      </p>
                    )}
                  </div>
                </a>
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}
